/**
 * Firebase App Check activation — MONITORING MODE ONLY.
 *
 * WHY THIS EXISTS
 *
 * Bots create Firebase email/password accounts by calling the Identity Platform
 * REST API directly with the public web API key. The intended control
 * (reCAPTCHA Enterprise bot protection on Identity Platform) is blocked on an
 * IAM grant only a project Owner can make, so App Check is the alternative: it
 * makes *this* client attach an `X-Firebase-AppCheck` header, and the console
 * then reports what share of traffic is verified. Nothing is enforced until an
 * operator turns enforcement on, and enforcement stays OFF until every client
 * (web, admin, iOS, Android) is reporting near-100% verified.
 *
 * WHY THERE IS NO `firebase/app-check` IMPORT HERE
 *
 * `assets/firebase.js` keeps the whole SDK out of the eager bundle (113 KB) by
 * loading it through one dynamic `import()` of `assets/firebase-api.js`. A
 * static `firebase/app-check` import in this file would put it back, because
 * `assets/firebase.js` imports *this* module statically. So the App Check
 * symbols arrive as the `authApi` namespace parameter, and the only static
 * `firebase/app-check` import in `src/` is the one in `firebase-api.js`.
 * Everything below is plain JS with no Firebase in its module graph.
 *
 * @see assets/firebase.js — the lazy loader and the SSR rule
 * @see assets/firebase-api.js — the one permitted static-import file
 * @see work/01d-app-check/web-runbook.md — the console steps this pairs with
 */

/**
 * Upper bound on how long Firebase Auth will wait for an App Check token
 * before giving up and sending the request unverified.
 *
 * THIS CONSTANT IS THE REASON LOGIN CANNOT BREAK. `@firebase/app-check` loads
 * `https://www.google.com/recaptcha/enterprise.js` with `script.onload` and
 * **no `onerror` and no timeout** (`loadReCAPTCHAEnterpriseScript`), and
 * resolves its `initialized` promise only from that `onload`. If the script is
 * blocked — ad-blocker, corporate proxy, captive portal, Google unreachable —
 * `provider.getToken()` never settles. Firebase Auth `await`s that token while
 * building the headers for *every* Identity Platform request
 * (`AuthImpl._getAppCheckToken`), so an unbounded wait there is an unbounded
 * hang on sign-in. Racing the provider against a deadline converts that hang
 * into a rejection, which App Check core already handles by returning a dummy
 * token plus an error — Auth logs a warning and sends the request without the
 * header. That is exactly the pre-App-Check behaviour.
 *
 * 5s is far above the normal path (script + `execute` + token exchange is
 * typically 300–700 ms on a warm connection) and low enough that the worst
 * case is a noticeable-but-usable sign-in rather than an apparent hang.
 */
const APP_CHECK_TOKEN_TIMEOUT_MS = 5000;

/**
 * Set once App Check has been activated for the default app. `initializeAppCheck`
 * throws `appCheck/already-initialized` on a second call with a different
 * provider instance, and `assets/firebase.js` deliberately clears its memo on
 * failure so a user retry re-runs initialisation.
 * @type {object|null}
 */
let appCheckInstance = null;

/**
 * The reCAPTCHA Enterprise site key, or `''` when App Check must stay dark.
 *
 * SHIP-DARK CONTRACT: blank or unset means App Check is never initialised —
 * no provider, no script tag, no network call, no console output, and the
 * bundle behaves exactly as it did before this change. That is what makes this
 * change safe to deploy ahead of any Firebase console work.
 *
 * @returns {string} the trimmed site key, or an empty string.
 */
export function readAppCheckSiteKey() {
    const siteKey = process.env.REACT_APP_APPCHECK_RECAPTCHA_KEY;
    return typeof siteKey === 'string' ? siteKey.trim() : '';
}

/**
 * Build a reCAPTCHA Enterprise provider whose `getToken` can never hang.
 *
 * Composition rather than `class extends`: the method is replaced on the
 * instance, so `initialize()`, `isEqual()` and the provider's internal
 * throttling state are untouched and no ES5-class-extends transpilation
 * question arises under CRA 3 / babel.
 *
 * @param {object} params
 * @param {Function} params.ReCaptchaEnterpriseProvider - the SDK constructor.
 * @param {string} params.siteKey - reCAPTCHA Enterprise score-based site key.
 * @returns {object} a provider instance safe to hand to `initializeAppCheck`.
 */
function createTimeBoxedProvider({ ReCaptchaEnterpriseProvider, siteKey }) {
    const provider = new ReCaptchaEnterpriseProvider(siteKey);
    const getTokenFromSdk = provider.getToken.bind(provider);

    provider.getToken = async function getTokenWithDeadline() {
        let timer = null;
        const deadline = new Promise((resolve, reject) => {
            timer = setTimeout(() => {
                reject(new Error('App Check token request timed out.'));
            }, APP_CHECK_TOKEN_TIMEOUT_MS);
        });

        const pending = getTokenFromSdk();
        // Deliberate departure from "no `.catch` chains": when the deadline
        // wins the race this promise is orphaned and may reject later with
        // nobody awaiting it. Swallowing it here prevents an
        // `unhandledrejection` that would otherwise reach the global handler.
        pending.catch(() => {});

        try {
            return await Promise.race([pending, deadline]);
        } finally {
            clearTimeout(timer);
        }
    };

    return provider;
}

/**
 * Activate App Check on an already-initialised `FirebaseApp`.
 *
 * **This function never throws and never rejects.** App Check is an observability
 * measure in monitoring mode; nothing about sign-in may depend on it. Callers
 * may `await` it on the auth path without any error handling of their own.
 *
 * Must only be called in the browser — it is reached exclusively from the
 * client-only dynamic-import path in `assets/firebase.js`, and additionally
 * guards on `window` so that a future caller cannot run it inside
 * `renderToString`, where `document` does not exist.
 *
 * @param {object} params
 * @param {object} params.app - the `FirebaseApp` returned by `initializeApp`.
 *   Must be the same instance `getAuth` was given, or Auth will not see the
 *   App Check provider.
 * @param {object} params.authApi - the `assets/firebase-api` namespace,
 *   supplying `initializeAppCheck` and `ReCaptchaEnterpriseProvider`.
 * @returns {Promise<object|null>} the `AppCheck` instance, or `null` when App
 *   Check is unconfigured, unavailable, or failed to start.
 */
export async function activateAppCheck({ app, authApi }) {
    if (appCheckInstance) return appCheckInstance;

    const siteKey = readAppCheckSiteKey();
    if (!siteKey) return null; // ship-dark: not configured, do nothing at all
    if (typeof window === 'undefined') return null; // never during SSR
    if (!app || !authApi || !authApi.initializeAppCheck) return null;

    try {
        appCheckInstance = authApi.initializeAppCheck(app, {
            provider: createTimeBoxedProvider({
                ReCaptchaEnterpriseProvider: authApi.ReCaptchaEnterpriseProvider,
                siteKey,
            }),
            // Keeps a fresh token in the background so a token fetch is not on
            // the critical path of each subsequent authenticated request.
            isTokenAutoRefreshEnabled: true,
        });
        return appCheckInstance;
    } catch (error) {
        // Swallowed on purpose, and this is not the "empty catch" the root
        // CLAUDE.md forbids: the only correct response to App Check failing to
        // start is to carry on without it. Surfacing it would put a bot-metrics
        // feature in front of the user's sign-in. The SDK has already logged
        // the detail to the console for whoever is debugging.
        appCheckInstance = null;
        return null;
    }
}

export default activateAppCheck;
